import {
  QuerySnapshot,
  DocumentSnapshot,
  DocumentData,
  CollectionReference,
  QueryConstraint,
  QueryDocumentSnapshot,
  query,
  limit,
  startAfter,
  getDocs,
} from '@firebase/firestore';
import type { ZodType } from 'zod';
import { createPrefixedLogger } from './logger';

const log = createPrefixedLogger('utils:firestore');

/**
 * Map a single Firestore document snapshot to a typed object with `id`.
 * Returns null if the document doesn't exist or fails schema validation.
 * Logs validation failures so the UI can render its empty/error state
 * instead of crashing on bad data.
 */
export function mapDoc<S extends ZodType>(
  snapshot: DocumentSnapshot<DocumentData>,
  schema: S
): import('zod').output<S> | null {
  if (!snapshot.exists()) return null;
  // `serverTimestamps: 'estimate'` fills pending serverTimestamp() fields with
  // a local Timestamp so optimistic snapshots don't fail schema validation.
  const candidate = { id: snapshot.id, ...snapshot.data({ serverTimestamps: 'estimate' }) };

  const result = schema.safeParse(candidate);
  if (!result.success) {
    log.error(`Schema validation failed for ${snapshot.ref.path}:`, result.error.issues);
    return null;
  }
  return result.data;
}

/**
 * Map a Firestore query snapshot to an array of typed objects with `id`.
 *
 * Docs that fail validation are dropped (and logged) rather than poisoning
 * the whole list. This trades strictness for resilience — a single bad doc
 * doesn't break the feed.
 */
export function mapDocs<S extends ZodType>(
  snapshot: QuerySnapshot<DocumentData>,
  schema: S
): import('zod').output<S>[] {
  const out: import('zod').output<S>[] = [];
  for (const doc of snapshot.docs) {
    const result = schema.safeParse({
      id: doc.id,
      ...doc.data({ serverTimestamps: 'estimate' }),
    });
    if (result.success) {
      out.push(result.data);
    } else {
      log.error(
        `Schema validation failed for ${doc.ref.path}; dropping from result set:`,
        result.error.issues
      );
    }
  }
  return out;
}

/**
 * Execute a paginated Firestore query and return typed results with cursor.
 * Each doc is runtime-validated against the provided Zod schema.
 */
export async function paginatedQuery<S extends ZodType>(
  collectionRef: CollectionReference<DocumentData>,
  constraints: QueryConstraint[],
  lastDoc: QueryDocumentSnapshot<DocumentData> | undefined,
  pageSize: number | undefined,
  schema: S
): Promise<{
  items: import('zod').output<S>[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
}> {
  let q = query(collectionRef, ...constraints, limit(pageSize ?? 20));
  if (lastDoc) {
    q = query(q, startAfter(lastDoc));
  }
  const snapshot = await getDocs(q);
  const items = mapDocs(snapshot, schema);
  return {
    items,
    lastDoc: snapshot.docs[snapshot.docs.length - 1] ?? null,
  };
}
